/* eslint-disable */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { Coffee, ChefHat, UtensilsCrossed, User, Clock, X, Plus, CheckCircle, Circle, Edit2, AlertTriangle, Calendar, FileText, Award, Star, Send, Copy, Check, Search, ClipboardList } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import LeaveOT from './LeaveOT';
import LeaveCalendar from './LeaveCalendar';
import Tips from './Tips';
import { C, VENUES, DEPTS, CONTRACT_TYPES, EMP_TYPES, CONTRACT_HRS, BAR_SKILLS, KIT_SKILLS, UNIFORM_DEPOSIT, emptyForm, fmt, fmtDT, addMonths, daysUntil, daysSince, midDate, isNew, pulseAlert, contractAlert, genToken, depositAmount, pill, inp, lbl, useIsMobile, RoleIcon, BalancePill, MemberDocButton } from './teamUtils';
import MemberModal from './MemberModal';

// ══════════════════════════════════════════════════════════════════════
// SEND FORM MODAL
// ══════════════════════════════════════════════════════════════════════
function SendFormModal({onClose,currentUser,member}) {
  const [email,setEmail]=useState('');
  const [sending,setSending]=useState(false);
  const [sent,setSent]=useState(false);
  const [link,setLink]=useState('');
  const [copied,setCopied]=useState(false);
  const [error,setError]=useState('');

  // Manager-input fields
  const [venue,setVenue]=useState('Box Jordaan');
  const [dept,setDept]=useState('FOH');
  const [role,setRole]=useState('');
  const [employmentType,setEmploymentType]=useState('Full Time');
  const [contractHours,setContractHours]=useState(38);
  const [contractStartDate,setContractStartDate]=useState('');
  const [grossSalary,setGrossSalary]=useState('');
  const [hourlyRate,setHourlyRate]=useState('14.71');

  const roleOptions = dept==='KIT' ? KIT_SKILLS : BAR_SKILLS;

  async function handleSend() {
    if (!email.trim()){setError('Please enter an email address.');return;}
    if (!role){setError('Please select a role.');return;}
    if (!contractStartDate){setError('Please enter a contract start date.');return;}
    if (employmentType==='Full Time'&&!grossSalary){setError('Please enter the gross monthly salary.');return;}
    if (employmentType==='Part Time'&&!hourlyRate){setError('Please enter the hourly rate.');return;}
    setSending(true);setError('');
    const token=genToken();
    const formUrl=`${window.location.origin}/onboarding/${token}`;
    const {error:dbErr}=await supabase.from('new_hire_submissions').insert({
      token,sent_to:email.trim(),sent_by:currentUser?.name||'Manager',status:'pending',
      dob:member?.birthday||null,
      venue,
      dept,
      role,
      employment_type:employmentType,
      contract_hours:employmentType==='Full Time'?contractHours:null,
      contract_start_date:contractStartDate||null,
      gross_monthly_salary:employmentType==='Full Time'?(parseFloat(grossSalary)||null):null,
      hourly_rate:employmentType==='Part Time'?(parseFloat(hourlyRate)||null):null,
    });
    if (dbErr){setError('Failed to create form: '+dbErr.message);setSending(false);return;}
    const {error:fnErr}=await supabase.functions.invoke('send-newhire-email',{
      body:{to:email.trim(),formUrl,sentBy:currentUser?.name||'Manager'},
    });
    if (fnErr) setError('Email failed — share the link below manually:');
    setLink(formUrl);setSent(true);setSending(false);
  }

  async function copyLink() {
    await navigator.clipboard.writeText(link);
    setCopied(true);setTimeout(()=>setCopied(false),2000);
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:600,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={onClose}>
      <div style={{background:C.white,borderRadius:'16px',width:'100%',maxWidth:'460px',boxShadow:'0 24px 64px rgba(0,0,0,0.2)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'20px 24px',background:C.navy,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontWeight:700,fontSize:'15px',color:C.white}}>Send New Hire Form</div>
            <div style={{fontSize:'12px',color:C.skyBlue,marginTop:'2px'}}>New employee fills in their own details</div>
          </div>
          <button onClick={onClose} style={{background:'rgba(255,255,255,0.12)',border:'none',borderRadius:'8px',cursor:'pointer',color:C.white,padding:'6px',display:'flex'}}><X size={18}/></button>
        </div>
        <div style={{padding:'24px'}}>
          {!sent?(
            <>
              <p style={{margin:'0 0 16px',fontSize:'13px',color:C.mid,lineHeight:1.6}}>
                Fill in the role details below, then enter the new hire’s email. They’ll complete their personal details — everything will pre-fill their team member card.
              </p>

              <div style={{background:C.cream,borderRadius:'10px',padding:'14px 16px',marginBottom:'16px',border:`1px solid ${C.border}`}}>
                <div style={{fontSize:'11px',fontWeight:700,color:C.mid,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'12px'}}>Role Details</div>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'10px'}}>
                  <div>
                    <label style={lbl}>Venue</label>
                    <select value={venue} onChange={e=>setVenue(e.target.value)} style={{...inp}}>
                      {VENUES.map(v=><option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Department</label>
                    <select value={dept} onChange={e=>{setDept(e.target.value);setRole('');}} style={{...inp}}>
                      {DEPTS.filter(d=>d!=='Management').map(d=><option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{marginBottom:'10px'}}>
                  <label style={lbl}>Role</label>
                  <select value={role} onChange={e=>setRole(e.target.value)} style={{...inp}}>
                    <option value=''>Select role…</option>
                    {roleOptions.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <div>
                    <label style={lbl}>Employment Type</label>
                    <select value={employmentType} onChange={e=>setEmploymentType(e.target.value)} style={{...inp}}>
                      {EMP_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {employmentType==='Full Time'&&(
                    <div>
                      <label style={lbl}>Contract Hours / week</label>
                      <select value={contractHours} onChange={e=>setContractHours(Number(e.target.value))} style={{...inp}}>
                        {CONTRACT_HRS.map(h=><option key={h} value={h}>{h}h</option>)}
                      </select>
                    </div>
                  )}
                </div>

                <div style={{marginTop:'10px'}}>
                  <label style={lbl}>Contract Start Date</label>
                  <input type='date' value={contractStartDate} onChange={e=>setContractStartDate(e.target.value)} style={{...inp}}/>
                </div>

                <div style={{marginTop:'10px'}}>
                  {employmentType==='Full Time'?(
                    <>
                      <label style={lbl}>Gross Monthly Salary (€)</label>
                      <input type='number' min='0' step='0.01' value={grossSalary} onChange={e=>setGrossSalary(e.target.value)} placeholder='e.g. 2400.00' style={{...inp}}/>
                    </>
                  ):(
                    <>
                      <label style={lbl}>Hourly Rate (€)</label>
                      <input type='number' min='0' step='0.01' value={hourlyRate} onChange={e=>setHourlyRate(e.target.value)} placeholder='€14.71' style={{...inp}}/>
                      <div style={{fontSize:'11px',color:C.mid,marginTop:'4px'}}>🇳🇱 Min. wage 2025: <strong>€14.71/hr</strong></div>
                    </>
                  )}
                </div>
              </div>

              <label style={lbl}>New hire’s email address</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSend()} placeholder="jan@email.com" style={{...inp,marginBottom:'12px'}}/>
              {error&&<div style={{fontSize:'12px',color:C.red,marginBottom:'12px'}}>{error}</div>}
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={handleSend} disabled={sending||!email.trim()||!role||!contractStartDate||(employmentType==='Full Time'?!grossSalary:!hourlyRate)}
                  style={{flex:1,padding:'10px',background:sending||!email.trim()||!role||!contractStartDate||(employmentType==='Full Time'?!grossSalary:!hourlyRate)?C.border:C.navy,color:C.white,border:'none',borderRadius:'8px',cursor:sending||!email.trim()||!role||!contractStartDate||(employmentType==='Full Time'?!grossSalary:!hourlyRate)?'not-allowed':'pointer',fontWeight:600,fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                  <Send size={14}/>{sending?'Sending…':'Send Form'}
                </button>
                <button onClick={onClose} style={{padding:'10px 16px',border:`1px solid ${C.border}`,borderRadius:'8px',background:C.white,cursor:'pointer',fontSize:'13px',color:C.mid}}>Cancel</button>
              </div>
            </>
          ):(
            <>
              <div style={{textAlign:'center',marginBottom:'20px'}}>
                <div style={{width:'48px',height:'48px',borderRadius:'50%',background:C.greenLight,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
                  <Check size={24} color={C.green}/>
                </div>
                <div style={{fontWeight:700,fontSize:'15px',color:C.charcoal,marginBottom:'4px'}}>Form sent to {email}</div>
                <div style={{fontSize:'13px',color:C.mid}}>You'll see an alert here when they submit it.</div>
              </div>
              {error&&<div style={{fontSize:'12px',color:C.amber,marginBottom:'12px',padding:'8px 12px',background:C.amberLight,borderRadius:'8px'}}>{error}</div>}
              <div style={{background:C.cream,borderRadius:'8px',padding:'12px 14px',marginBottom:'16px'}}>
                <div style={{fontSize:'11px',fontWeight:700,color:C.mid,textTransform:'uppercase',marginBottom:'6px'}}>Form link</div>
                <div style={{fontSize:'12px',color:C.charcoal,wordBreak:'break-all',marginBottom:'8px'}}>{link}</div>
                <button onClick={copyLink} style={{display:'flex',alignItems:'center',gap:'6px',padding:'6px 12px',background:copied?C.greenLight:C.white,border:`1px solid ${copied?C.green:C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px',color:copied?C.green:C.charcoal,fontWeight:500}}>
                  {copied?<Check size={13}/>:<Copy size={13}/>}{copied?'Copied!':'Copy link'}
                </button>
              </div>
              <button onClick={onClose} style={{width:'100%',padding:'10px',background:C.navy,color:C.white,border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'13px'}}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SwimlaneCard({member,badge,onClick}) {
  return (
    <div onClick={()=>onClick(member)} style={{background:C.white,borderRadius:'10px',padding:'12px 14px',border:`1px solid ${C.border}`,cursor:'pointer',transition:'box-shadow 0.15s'}}
      onMouseEnter={e=>e.currentTarget.style.boxShadow='0 3px 10px rgba(0,0,0,0.1)'}
      onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
      <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
        <RoleIcon dept={member.dept} size={28}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:'13px',color:C.charcoal,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{member.name}</div>
          <div style={{fontSize:'11px',color:C.mid}}>{member.venue.replace('Box ','')}</div>
        </div>
        {badge}
      </div>
    </div>
  );
}

function AlertSwimlanes({members,onOpen,onDismiss,isSupervisor}) {
  const [deptF,setDeptF]=useState('All');
  const [rowOpen,setRowOpen]=useState({});

  const newMs  = members.filter(m=>m.active&&!m.probation_done);
  const pulseMs= isSupervisor?[]:members.filter(m=>!!pulseAlert(m)&&!m.pulse_dismissed);
  const contMs = isSupervisor?[]:members.filter(m=>!!contractAlert(m)&&!m.renewal_dismissed);
  const total  = newMs.length+pulseMs.length+contMs.length;

  if (total===0) return null;

  const dismiss = (field, memberId) => onDismiss && onDismiss(field, memberId);
  const filterDept = ms => deptF==='All' ? ms : ms.filter(m=>m.dept===deptF);

  const cols=[
    {key:'new',label:'Onboarding Incomplete',color:C.green,
      members:filterDept(newMs),
      badge:m=>{const d=daysSince(m.join_date||m.contract_start_date);return <span style={{padding:'3px 8px',borderRadius:'20px',background:C.greenLight,color:C.green,fontSize:'11px',fontWeight:700,border:`1px solid ${C.green}44`}}>{d!=null?`Day ${d}`:'—'}</span>;}},
    {key:'pulse',label:'Pulse Check Due',color:C.skyText,
      members:filterDept(pulseMs),dismiss:(m)=>dismiss('pulse_dismissed',m.id),
      badge:m=>{const a=pulseAlert(m);return <span style={{padding:'3px 8px',borderRadius:'20px',background:a.urgency==='high'?C.redLight:C.amberLight,color:a.urgency==='high'?C.red:C.amber,fontSize:'11px',fontWeight:700}}>{a.label}</span>;}},
    {key:'contract',label:'Contract Ending',color:C.red,
      members:filterDept(contMs),dismiss:(m)=>dismiss('renewal_dismissed',m.id),
      badge:m=>{const a=contractAlert(m);return <span style={{padding:'3px 8px',borderRadius:'20px',background:a.urgency==='high'?C.redLight:C.amberLight,color:a.urgency==='high'?C.red:C.amber,fontSize:'11px',fontWeight:700}}>{a.label}</span>;}},
  ].filter(c=>c.members.length>0);

  return (
    <div style={{marginBottom:'20px',borderRadius:'12px',border:`1px solid ${C.amber}33`,overflow:'hidden'}}>

      {/* ── Header — always visible, no toggle ── */}
      <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'11px 16px',background:C.amberLight}}>
        <AlertTriangle size={14} color={C.amber} style={{flexShrink:0}}/>
        <span style={{fontSize:'13px',fontWeight:700,color:C.charcoal}}>Action Required</span>
        <span style={{padding:'2px 8px',borderRadius:'20px',background:C.amber+'22',color:C.amber,fontSize:'11px',fontWeight:700,flexShrink:0}}>{total}</span>
        <div style={{flex:1}}/>
        <div style={{display:'flex',gap:'4px',flexShrink:0}}>
          {['All','FOH','KIT'].map(d=>(
            <button key={d} onClick={()=>setDeptF(d)} style={{
              padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:600,cursor:'pointer',border:'none',
              background:deptF===d?C.navy:'rgba(255,255,255,0.6)',
              color:deptF===d?C.white:C.mid,
              transition:'all 0.15s',
            }}>{d}</button>
          ))}
        </div>
      </div>

      {/* ── Category rows — always visible, collapsed by default ── */}
      <div style={{padding:'8px 10px',background:C.white,display:'flex',flexDirection:'column',gap:'6px'}}>
        {cols.map(col=>{
          const isOpen = !!rowOpen[col.key];
          return (
            <div key={col.key} style={{borderRadius:'10px',border:`1px solid ${col.color}33`,overflow:'hidden'}}>
              <div
                onClick={()=>setRowOpen(p=>({...p,[col.key]:!p[col.key]}))}
                style={{display:'flex',alignItems:'center',gap:'8px',padding:'11px 14px',background:col.color+'10',borderLeft:`3px solid ${col.color}`,cursor:'pointer',userSelect:'none'}}
              >
                <span style={{fontSize:'13px',fontWeight:700,color:col.color,flex:1}}>{col.label}</span>
                <span style={{padding:'1px 8px',borderRadius:'20px',background:col.color+'22',color:col.color,fontSize:'12px',fontWeight:700,flexShrink:0}}>{col.members.length}</span>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" style={{flexShrink:0,transform:isOpen?'rotate(180deg)':'rotate(0deg)',transition:'transform 0.2s'}}>
                  <path d="M2.5 5L7 9.5L11.5 5" stroke={col.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              {isOpen && (
                <div style={{padding:'8px 10px',display:'flex',flexDirection:'column',gap:'6px',background:col.color+'06'}}>
                  {col.members.length > 0
                    ? col.members.map((m,i)=><SwimlaneCard key={i} member={m} badge={col.badge(m)} onClick={onOpen}/>)
                    : <div style={{padding:'8px 4px',fontSize:'12px',color:C.mid,fontStyle:'italic'}}>None for this filter</div>
                  }
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SUBMISSIONS MODAL
// ══════════════════════════════════════════════════════════════════════
function SubmissionsModal({submissions,onClose,onConvert,onDismiss,isAdmin}) {
  const [selected,setSelected]=useState(null);
  const [showSensitive,setShowSensitive]=useState(false);
  const [docLoading,setDocLoading]=useState({});
  const [dismissing,setDismissing]=useState({});

  const submitted=submissions.filter(s=>s.status==='submitted');
  const pending=submissions.filter(s=>s.status==='pending');

  async function openDoc(path, key) {
    if (!path) return;
    setDocLoading(p=>({...p,[key]:true}));
    const { data, error } = await supabase.storage.from('new-hire-docs').createSignedUrl(path, 60);
    setDocLoading(p=>({...p,[key]:false}));
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    } else {
      alert('Could not load document: ' + (error?.message || 'Unknown error'));
    }
  }

  // ── Detail view for a submitted form ──
  if (selected) return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:700,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={()=>setSelected(null)}>
      <div style={{background:C.white,borderRadius:'16px',width:'100%',maxWidth:'560px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 24px 64px rgba(0,0,0,0.22)'}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'20px 24px',background:C.navy,display:'flex',alignItems:'center',justifyContent:'space-between',borderRadius:'16px 16px 0 0'}}>
          <div>
            <div style={{fontWeight:700,fontSize:'15px',color:C.white}}>{selected.first_name} {selected.last_name}</div>
            <div style={{fontSize:'12px',color:C.skyBlue,marginTop:'2px'}}>Submitted {selected.submitted_at?new Date(selected.submitted_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}):''}</div>
          </div>
          <button onClick={()=>setSelected(null)} style={{background:'rgba(255,255,255,0.12)',border:'none',borderRadius:'8px',cursor:'pointer',color:C.white,padding:'6px',display:'flex'}}><X size={18}/></button>
        </div>
        <div style={{padding:'24px',display:'flex',flexDirection:'column',gap:'16px'}}>
          <div style={{background:C.cream,borderRadius:'12px',padding:'16px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[['First Name',selected.first_name],['Last Name',selected.last_name],['Date of Birth',selected.dob],['Nationality',selected.nationality],['Phone',selected.phone],['Email',selected.email],['Address',[selected.street,selected.zip,selected.city].filter(Boolean).join(', ')]].map(([l,v])=>(
              <div key={l} style={{gridColumn:l==='Address'?'span 2':'auto'}}>
                <div style={{fontSize:'10px',fontWeight:700,color:C.mid,textTransform:'uppercase',marginBottom:'3px'}}>{l}</div>
                <div style={{fontSize:'13px',fontWeight:600,color:C.charcoal}}>{v||'—'}</div>
              </div>
            ))}
          </div>
          {isAdmin&&(
            <div>
              <button onClick={()=>setShowSensitive(p=>!p)} style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 14px',background:showSensitive?C.navy:C.white,color:showSensitive?C.white:C.charcoal,border:`1px solid ${C.border}`,borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:600,marginBottom:showSensitive?'10px':'0'}}>
                {showSensitive?<CheckCircle size={13} color={C.white}/>:<Circle size={13}/>} {showSensitive?'Hide':'Show'} Sensitive Info (BSN & IBAN)
              </button>
              {showSensitive&&(
                <div style={{background:C.redLight,border:`1px solid ${C.red}33`,borderRadius:'10px',padding:'14px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                  {[['BSN',selected.bsn],['IBAN',selected.iban]].map(([l,v])=>(
                    <div key={l}>
                      <div style={{fontSize:'10px',fontWeight:700,color:C.red,textTransform:'uppercase',marginBottom:'3px'}}>{l}</div>
                      <div style={{fontSize:'14px',fontWeight:700,color:C.charcoal,fontFamily:'monospace'}}>{v||'—'}</div>
                    </div>
                  ))}
                  <div style={{gridColumn:'span 2',fontSize:'11px',color:C.red,fontStyle:'italic'}}>Handle with care — do not share or screenshot</div>
                </div>
              )}
            </div>
          )}
          {isAdmin&&(selected.id_doc_path||selected.bsn_doc_path)&&(
            <div>
              <div style={{fontSize:'11px',fontWeight:700,color:C.mid,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'8px'}}>Uploaded Documents</div>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                {selected.id_doc_path&&(
                  <button onClick={()=>openDoc(selected.id_doc_path,'id')} disabled={docLoading['id']}
                    style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 14px',background:C.skyBlueLight,border:`1px solid ${C.skyBlue}44`,borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:600,color:C.skyText}}>
                    {docLoading['id']?'Loading…':'🪪 View ID / Passport'}
                  </button>
                )}
                {selected.bsn_doc_path&&(
                  <button onClick={()=>openDoc(selected.bsn_doc_path,'bsn')} disabled={docLoading['bsn']}
                    style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 14px',background:C.skyBlueLight,border:`1px solid ${C.skyBlue}44`,borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:600,color:C.skyText}}>
                    {docLoading['bsn']?'Loading…':'📄 View BSN Document'}
                  </button>
                )}
              </div>
              <div style={{fontSize:'11px',color:C.mid,marginTop:'6px',fontStyle:'italic'}}>Opens in a new tab · link expires after 60 seconds</div>
            </div>
          )}
          <div style={{display:'flex',gap:'10px',paddingTop:'4px'}}>
            {/* onConvert now handles marking converted ONLY after handleSave succeeds */}
            <button onClick={()=>onConvert(selected)} style={{flex:1,padding:'10px',background:C.navy,color:C.white,border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'13px'}}>
              Create Team Member Card →
            </button>
            <button onClick={()=>setSelected(null)} style={{padding:'10px 16px',border:`1px solid ${C.border}`,borderRadius:'8px',background:C.white,cursor:'pointer',fontSize:'13px',color:C.mid}}>Back</button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── List view: two sections ──
  const SectionLabel = ({label,count,color}) => (
    <div style={{fontSize:'11px',fontWeight:700,color,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'8px',display:'flex',alignItems:'center',gap:'6px'}}>
      <span style={{width:7,height:7,borderRadius:'50%',background:color,display:'inline-block'}}/>
      {label} ({count})
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:700,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={onClose}>
      <div style={{background:C.white,borderRadius:'16px',width:'100%',maxWidth:'500px',boxShadow:'0 24px 64px rgba(0,0,0,0.22)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'20px 24px',background:C.navy,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontWeight:700,fontSize:'15px',color:C.white}}>New Hire Forms</div>
            <div style={{fontSize:'12px',color:C.skyBlue,marginTop:'2px'}}>
              {submitted.length>0&&`${submitted.length} to action`}
              {submitted.length>0&&pending.length>0&&' · '}
              {pending.length>0&&`${pending.length} sent, awaiting response`}
            </div>
          </div>
          <button onClick={onClose} style={{background:'rgba(255,255,255,0.12)',border:'none',borderRadius:'8px',cursor:'pointer',color:C.white,padding:'6px',display:'flex'}}><X size={18}/></button>
        </div>
        <div style={{padding:'16px 20px',display:'flex',flexDirection:'column',gap:'20px',maxHeight:'65vh',overflowY:'auto'}}>

          {/* ── Submitted — needs action ── */}
          {submitted.length>0&&(
            <div>
              <SectionLabel label="Submitted — action required" count={submitted.length} color={C.amber}/>
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                {submitted.map(s=>(
                  <div key={s.id} onClick={()=>setSelected(s)}
                    style={{display:'flex',alignItems:'center',gap:'12px',padding:'12px 14px',background:C.amberLight,borderRadius:'10px',cursor:'pointer',border:`1px solid ${C.amber}33`,transition:'box-shadow 0.15s'}}
                    onMouseEnter={e=>e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.08)'}
                    onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                    <div style={{width:36,height:36,borderRadius:'50%',background:C.orange+'22',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'14px',color:C.amber,flexShrink:0,border:`1px solid ${C.amber}44`}}>
                      {(s.first_name?.[0]||'?').toUpperCase()}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:'13px',color:C.charcoal}}>{s.first_name&&s.last_name?`${s.first_name} ${s.last_name}`:s.sent_to}</div>
                      <div style={{fontSize:'11px',color:C.mid}}>Submitted {s.submitted_at?new Date(s.submitted_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'}):''} · {s.sent_to}</div>
                    </div>
                    <span style={{fontSize:'12px',color:C.amber,fontWeight:700}}>Review →</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Pending — sent, not yet filled ── */}
          {pending.length>0&&(
            <div>
              <SectionLabel label="Sent — awaiting response" count={pending.length} color={C.mid}/>
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                {pending.map(s=>(
                  <div key={s.id}
                    style={{display:'flex',alignItems:'center',gap:'12px',padding:'10px 14px',background:C.cream,borderRadius:'10px',border:`1px solid ${C.border}`}}>
                    <div style={{width:32,height:32,borderRadius:'50%',background:C.white,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'12px',color:C.mid,flexShrink:0,border:`1px solid ${C.border}`}}>
                      {(s.sent_to?.[0]||'?').toUpperCase()}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,fontSize:'13px',color:C.charcoal}}>{s.sent_to}</div>
                      <div style={{fontSize:'11px',color:C.mid}}>Sent {s.created_at?new Date(s.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'}):''}{s.sent_by?` by ${s.sent_by}`:''}</div>
                    </div>
                    <span style={{fontSize:'11px',padding:'2px 8px',background:C.white,border:`1px solid ${C.border}`,borderRadius:'20px',color:C.mid,fontWeight:600,flexShrink:0}}>Awaiting</span>
                    {isAdmin&&(
                      <button
                        disabled={dismissing[s.id]}
                        onClick={async()=>{
                          setDismissing(p=>({...p,[s.id]:true}));
                          const{error}=await supabase.from('new_hire_submissions').update({status:'dismissed'}).eq('id',s.id);
                          if(!error&&onDismiss) onDismiss(s.id);
                          setDismissing(p=>({...p,[s.id]:false}));
                        }}
                        style={{padding:'4px 10px',background:'none',border:`1px solid ${C.border}`,borderRadius:'6px',cursor:dismissing[s.id]?'not-allowed':'pointer',fontSize:'11px',color:C.mid,fontWeight:600,flexShrink:0,opacity:dismissing[s.id]?0.5:1,whiteSpace:'nowrap'}}>
                        {dismissing[s.id]?'Dismissing…':'Dismiss'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {submitted.length===0&&pending.length===0&&(
            <div style={{padding:'40px',textAlign:'center',color:C.mid,fontSize:'13px'}}>No active forms</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// NEW HIRE BANNER
// ══════════════════════════════════════════════════════════════════════
function NewHireBanner({submissions,onOpen}) {
  const submitted=submissions.filter(s=>s.status==='submitted');
  const pending=submissions.filter(s=>s.status==='pending');
  if (submitted.length===0&&pending.length===0) return null;
  return (
    <div onClick={onOpen} style={{background:C.yellowLight,border:`1px solid ${C.orange}44`,borderRadius:'12px',padding:'14px 18px',marginBottom:'20px',display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',cursor:'pointer',transition:'box-shadow 0.15s'}}
      onMouseEnter={e=>e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.1)'}
      onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
      <Star size={16} color={C.amber} fill={C.amber}/>
      <div style={{flex:1,display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
        <span style={{fontWeight:700,fontSize:'13px',color:C.charcoal}}>New Hire Forms</span>
        {pending.length>0&&<><span style={{color:C.border}}>·</span><span style={{fontSize:'13px',color:C.mid}}>{pending.length} sent</span></>}
        {submitted.length>0&&<><span style={{color:C.border}}>·</span><span style={{fontSize:'13px',color:C.amber,fontWeight:600}}>{submitted.length} completed</span></>}
      </div>
      <span style={{fontSize:'12px',color:C.amber,fontWeight:600}}>View →</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ROSTER ROW
// ══════════════════════════════════════════════════════════════════════
function RosterRow({member,onClick,isAdmin}) {
  const pa=pulseAlert(member);
  const ca=contractAlert(member);
  const nw=isNew(member);
  const expired=daysUntil(member.contract_end_date)!==null&&daysUntil(member.contract_end_date)<0;
  const hi=(pa?.urgency==='high')||(ca?.urgency==='high');
  const bc=nw?C.green:hi?C.red:(pa||ca)?C.amber:C.border;

  const salaryDisplay = (() => {
    if (!isAdmin) return null;
    if (member.employment_type==='Full Time' && member.gross_monthly_salary) {
      return { label:'Salary', value:`€${Number(member.gross_monthly_salary).toLocaleString('nl-NL',{minimumFractionDigits:0,maximumFractionDigits:0})}/mo` };
    }
    if (member.employment_type==='Part Time' && member.hourly_rate) {
      return { label:'Rate', value:`€${Number(member.hourly_rate).toFixed(2)}/hr` };
    }
    return { label: member.employment_type==='Full Time' ? 'Salary' : 'Rate', value: '—' };
  })();

  return (
    <div onClick={()=>onClick(member)}
      style={{background:expired?C.redLight:C.white,borderRadius:'10px',border:`1px solid ${expired?C.red+'44':C.border}`,borderLeft:`3px solid ${bc}`,padding:'12px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',transition:'box-shadow 0.15s'}}
      onMouseEnter={e=>e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.08)'}
      onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
      <RoleIcon dept={member.dept} size={36}/>
      <div style={{minWidth:'140px',flex:'1 1 140px'}}>
        <div style={{fontWeight:700,fontSize:'14px',color:C.charcoal,display:'flex',alignItems:'center',gap:'6px'}}>
          {member.name}
          {nw&&<span style={{padding:'1px 7px',background:C.yellow,color:C.charcoal,borderRadius:'20px',fontSize:'10px',fontWeight:700}}>NEW</span>}
        </div>
        <div style={{fontSize:'12px',color:C.mid}}>{member.role||member.dept} · {member.venue}</div>
      </div>
      {(expired||pa||ca) && (
        <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
          {expired&&<span style={{padding:'3px 8px',background:C.red,color:C.white,borderRadius:'20px',fontSize:'11px',fontWeight:700}}>EXPIRED</span>}
          {pa&&<span style={{padding:'3px 8px',background:pa.urgency==='high'?C.redLight:C.amberLight,color:pa.urgency==='high'?C.red:C.amber,borderRadius:'20px',fontSize:'11px',fontWeight:600}}>⚑ Pulse</span>}
          {ca&&<span style={{padding:'3px 8px',background:ca.urgency==='high'?C.redLight:C.amberLight,color:ca.urgency==='high'?C.red:C.amber,borderRadius:'20px',fontSize:'11px',fontWeight:600}}>⚑ Contract</span>}
        </div>
      )}
      <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:'20px',flexWrap:'wrap'}}>
      <div style={{textAlign:'center',minWidth:'80px'}}>
        <div style={{fontSize:'10px',fontWeight:700,color:C.mid,textTransform:'uppercase',marginBottom:'2px'}}>Joined</div>
        <div style={{fontSize:'12px',color:C.charcoal}}>{fmt(member.join_date||member.contract_start_date)}</div>
      </div>
      <div style={{textAlign:'center',minWidth:'100px'}}>
        <div style={{fontSize:'10px',fontWeight:700,color:C.mid,textTransform:'uppercase',marginBottom:'2px'}}>Contract</div>
        {member.contract_type==='Permanent'
          ?<span style={{fontSize:'11px',background:C.greenLight,color:C.green,padding:'2px 8px',borderRadius:'20px',fontWeight:700}}>Permanent</span>
          :<div style={{fontSize:'12px',color:ca?C.red:C.charcoal}}>{fmt(member.contract_end_date)}</div>
        }
      </div>
      {salaryDisplay && (
        <div style={{textAlign:'center',minWidth:'80px'}}>
          <div style={{fontSize:'10px',fontWeight:700,color:C.mid,textTransform:'uppercase',marginBottom:'2px'}}>{salaryDisplay.label}</div>
          <div style={{fontSize:'12px',fontWeight:600,color:salaryDisplay.value==='—'?C.border:C.charcoal}}>{salaryDisplay.value}</div>
        </div>
      )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// EITJE MATCH FIELD — Eitje user ID input with "Find in Eitje" button
// ══════════════════════════════════════════════════════════════════════
function EitjeMatchField({ value, onChange, memberName, eitjeUsers, eitjeUsersLoaded }) {
  const [searching, setSearching]   = useState(false);
  const [matches,   setMatches]     = useState(null); // null = not searched, [] = no matches
  const [confirmed, setConfirmed]   = useState(null); // {user_name, last_seen, venues}

  // Strip accents/diacritics for fuzzy matching
  function norm(s){
    return (s||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9 ]/g,'').trim();
  }

  function findMatches() {
    if (!memberName?.trim()) return;
    setSearching(true);
    setMatches(null);

    const target = norm(memberName);
    const targetParts = target.split(/\s+/).filter(Boolean);

    // 1. Exact normalised match
    let hits = eitjeUsers.filter(u => norm(u.user_name) === target);

    // 2. Try comma-reversed ("Barbosa, Bárbara")
    if (hits.length === 0) {
      hits = eitjeUsers.filter(u => {
        const n = norm(u.user_name);
        const reversed = n.split(/\s+/).reverse().join(' ');
        return reversed === target;
      });
    }

    // 3. Match if all name parts present in either order
    if (hits.length === 0 && targetParts.length >= 2) {
      hits = eitjeUsers.filter(u => {
        const nParts = norm(u.user_name).split(/\s+/);
        return targetParts.every(p => nParts.includes(p));
      });
    }

    // 4. Final fallback: substring match on first part
    if (hits.length === 0 && targetParts[0]?.length >= 3) {
      hits = eitjeUsers.filter(u => {
        const n = norm(u.user_name);
        return targetParts.every(p => n.includes(p));
      });
    }

    // Sort by recency
    hits = [...hits].sort((a,b) => (b.last_seen||'').localeCompare(a.last_seen||''));

    setSearching(false);
    if (hits.length === 1) {
      onChange(hits[0].eitje_user_id);
      setConfirmed(hits[0]);
      setMatches([]);
    } else {
      setMatches(hits);
      setConfirmed(null);
    }
  }

  function pickMatch(u) {
    onChange(u.eitje_user_id);
    setConfirmed(u);
    setMatches([]);
  }

  const inputStyle = {
    flex:1, padding:'8px 10px', border:`1.5px solid ${C.border}`, borderRadius:'8px',
    fontSize:'13px', boxSizing:'border-box', background:C.white, color:C.charcoal, outline:'none', fontFamily:'inherit',
  };

  return (
    <div>
      <label style={lbl}>Eitje User ID <span style={{fontWeight:400,color:C.mid}}>(auto-find)</span></label>
      <div style={{display:'flex',gap:'6px'}}>
        <input
          value={value||''}
          onChange={e => { onChange(e.target.value); setConfirmed(null); setMatches(null); }}
          placeholder="e.g. 123456"
          style={inputStyle}
        />
        <button
          type="button"
          onClick={findMatches}
          disabled={!eitjeUsersLoaded || !memberName?.trim() || searching}
          title={!eitjeUsersLoaded ? 'Loading Eitje users…' : 'Find this person in Eitje hours data'}
          style={{
            display:'flex',alignItems:'center',gap:'4px',padding:'8px 12px',
            background: (!eitjeUsersLoaded || !memberName?.trim() || searching) ? C.border : C.skyText,
            color:C.white, border:'none', borderRadius:'8px',
            cursor: (!eitjeUsersLoaded || !memberName?.trim() || searching) ? 'not-allowed' : 'pointer',
            fontSize:'12px', fontWeight:600, whiteSpace:'nowrap',
          }}
        >
          <Search size={12}/>
          {searching ? '…' : 'Find'}
        </button>
      </div>

      {/* Confirmed match */}
      {confirmed && (
        <div style={{marginTop:'6px',padding:'6px 10px',background:C.greenLight,borderRadius:'6px',fontSize:'11px',color:C.green,display:'flex',alignItems:'center',gap:'6px'}}>
          <CheckCircle size={12}/>
          <span><strong>{confirmed.user_name}</strong> · {confirmed.venues?.join(', ')||'—'} · last shift {confirmed.last_seen}</span>
        </div>
      )}

      {/* Multiple matches — show picker */}
      {matches && matches.length > 1 && (
        <div style={{marginTop:'6px',padding:'8px 10px',background:C.amberLight,borderRadius:'6px',border:`1px solid ${C.amber}33`}}>
          <div style={{fontSize:'11px',color:C.amber,fontWeight:600,marginBottom:'6px'}}>
            {matches.length} possible matches — pick one:
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
            {matches.slice(0,5).map(u => (
              <button
                key={u.eitje_user_id}
                type="button"
                onClick={() => pickMatch(u)}
                style={{textAlign:'left',padding:'6px 10px',background:C.white,border:`1px solid ${C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px',color:C.charcoal}}
              >
                <strong>{u.user_name}</strong> <span style={{color:C.mid}}>· {u.venues?.join(', ')||'—'} · last {u.last_seen} · ID {u.eitje_user_id}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No matches */}
      {matches && matches.length === 0 && !confirmed && (
        <div style={{marginTop:'6px',padding:'6px 10px',background:C.redLight,borderRadius:'6px',fontSize:'11px',color:C.red}}>
          No match found in Eitje hours. Enter ID manually if you know it.
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════
export default function Team({currentUser}) {
  const isAdmin =currentUser?.role?.toLowerCase()==='admin';
  const isMgr   =currentUser?.role==='manager'||isAdmin;
  const isSupervisor=currentUser?.role==='supervisor'&&!isMgr;
  const isMobile=useIsMobile();

  const [tab,setTab]=useState('team');
  const [members,setMembers]=useState([]);
  const [trainings,setTrainings]=useState([]);
  const [completions,setCompletions]=useState([]);
  const [submissions,setSubmissions]=useState([]);
  const [loading,setLoading]=useState(true);

  const [search,setSearch]=useState('');
  const [vf,setVf]=useState('All');
  const [df,setDf]=useState('All');
  const [nmbrsF,setNmbrsF]=useState(false);
  const [contractF,setContractF]=useState(false);
  const [eitjeF,setEitjeF]=useState(false);

  const [modal,setModal]=useState(null);
  const [showSend,setShowSend]=useState(false);
  const [showForm,setShowForm]=useState(false);
  const [editId,setEditId]=useState(null);
  const [form,setForm]=useState(emptyForm);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState(null);
  const [delConfirm,setDelConfirm]=useState(null);
  const [showSubmissions,setShowSubmissions]=useState(false);
  const [showDeposits,setShowDeposits]=useState(false);
  const [eitjeUsers,setEitjeUsers]=useState([]);
  const [eitjeUsersLoaded,setEitjeUsersLoaded]=useState(false);

  const compEnd=form.contract_type==='Fixed Term'&&form.contract_start_date&&form.contract_months
    ?addMonths(form.contract_start_date,form.contract_months):null;

  const reloadCompletions=useCallback(async()=>{
    const{data}=await supabase.from('training_completions').select('*');
    if(data)setCompletions(data);
  },[]);

  async function loadMembers(){
    const{data:m}=await supabase.from('team_members').select('*').order('name');
    if(m)setMembers(m);
  }

  useEffect(()=>{
    async function load(){
      const[{data:m},{data:t},{data:c},{data:s}]=await Promise.all([
        supabase.from('team_members').select('*').order('name'),
        supabase.from('trainings').select('*').order('title'),
        supabase.from('training_completions').select('*'),
        supabase.from('new_hire_submissions').select('*').order('created_at',{ascending:false}),
      ]);
      if(m)setMembers(m);
      if(t)setTrainings(t||[]);
      if(c)setCompletions(c||[]);
      if(s)setSubmissions(s||[]);
      setLoading(false);
    }
    load();
  },[]);

  function onUpdate(u){
    if(u._refreshCompletions){reloadCompletions();return;}
    setMembers(p=>p.map(m=>m.id===u.id?u:m));
    if(modal?.id===u.id)setModal(u);
  }

  // Lazy-load distinct Eitje users when form is first opened — used by "Find in Eitje" button
  useEffect(()=>{
    if(!showForm||eitjeUsersLoaded)return;
    async function loadEitjeUsers(){
      const{data}=await supabase
        .from('eitje_hours')
        .select('eitje_user_id, user_name, venue, date')
        .not('eitje_user_id','is',null)
        .order('date',{ascending:false})
        .limit(20000); // last ~20k shifts is plenty to surface all active users
      if(!data){setEitjeUsersLoaded(true);return;}
      // Group by eitje_user_id, keep most recent shift info
      const map=new Map();
      data.forEach(r=>{
        if(!r.eitje_user_id)return;
        const k=String(r.eitje_user_id);
        if(!map.has(k))map.set(k,{eitje_user_id:k,user_name:r.user_name,last_seen:r.date,venues:new Set([r.venue].filter(Boolean)),shift_count:1});
        else{
          const e=map.get(k);
          e.shift_count++;
          if(r.venue)e.venues.add(r.venue);
        }
      });
      const list=Array.from(map.values()).map(e=>({...e,venues:Array.from(e.venues)}));
      setEitjeUsers(list);
      setEitjeUsersLoaded(true);
    }
    loadEitjeUsers();
  },[showForm,eitjeUsersLoaded]);

  const filtered=members.filter(m=>{
    if(vf!=='All'&&m.venue!==vf)return false;
    if(df!=='All'&&m.dept!==df)return false;
    if(search&&!m.name.toLowerCase().includes(search.toLowerCase()))return false;
    if(nmbrsF&&m.nmbrs_profile_done)return false;
    if(contractF&&m.contract_signed)return false;
    if(eitjeF&&m.eitje_user_id)return false;
    return true;
  });

  function handleEdit(member){
    let cm='';
    if(member.contract_start_date&&member.contract_end_date){
      const s=new Date(member.contract_start_date),e=new Date(member.contract_end_date);
      cm=Math.round((e-s)/(1000*60*60*24*30.44));
    }
    setForm({...emptyForm,...member,contract_months:cm,contract_hours:member.contract_hours||38,email:member.email||''});
    setEditId(member.id);setShowForm(true);setModal(null);setErr(null);
  }

  function cancelForm(){setShowForm(false);setEditId(null);setForm(emptyForm);setErr(null);}
  function toggleSkill(sk){setForm(p=>({...p,skills:p.skills.includes(sk)?p.skills.filter(s=>s!==sk):[...p.skills,sk]}));}

  async function handleSave(){
    if(!form.name||!form.join_date){setErr('Name and join date are required.');return;}
    setSaving(true);setErr(null);
    const end=form.contract_type==='Permanent'?null:(compEnd||form.contract_end_date||null);
    const payload={
      name:form.name.trim(),venue:form.venue,dept:form.dept,role:form.role?.trim()||'',
      employment_type:form.employment_type,contract_type:form.contract_type,
      contract_hours:form.employment_type==='Part Time'?0:(parseInt(form.contract_hours)||38),
      join_date:form.join_date,
      contract_start_date:form.contract_start_date||form.join_date,
      contract_end_date:end,
      contract_number:parseInt(form.contract_number)||1,
      skills:form.skills||[],
      probation_done:form.probation_done,mid_checkin_done:form.mid_checkin_done,
      contract_renewal:form.contract_renewal||false,
      birthday:form.birthday ? (form.birthday.includes('/') ? form.birthday.split('/').reverse().join('-') : form.birthday) : null, active:form.active,
      uniform_deposit_paid: form.uniform_deposit_paid || false,
      email: form.email||null,
      street: form.street||null,
      zip: form.zip||null,
      city: form.city||null,
      bsn: form.bsn||null,
      iban: form.iban||null,
      phone: form.phone||null,
      nationality: form.nationality||null,
      id_doc_path: form.id_doc_path||null,
      bsn_doc_path: form.bsn_doc_path||null,
      contract_signed: form.contract_signed || false,
      nmbrs_profile_done: form.nmbrs_profile_done || false,
      eitje_user_id: form.eitje_user_id?.toString().trim() || null,
      nmbrs_employee_number: form.nmbrs_employee_number ? parseInt(form.nmbrs_employee_number) : null,
      gross_monthly_salary: form.employment_type==='Full Time' ? (parseFloat(form.gross_monthly_salary)||null) : null,
      hourly_rate: form.employment_type==='Part Time' ? (parseFloat(form.hourly_rate)||null) : null,
      leave_accrual_pct: form.employment_type==='Full Time' ? (parseFloat(form.leave_accrual_pct) || 8.0) : null,
      balance_calc_start_date: form.balance_calc_start_date || null,
      opening_ot_balance:    form.employment_type==='Full Time' ? (parseFloat(form.opening_ot_balance)    || 0) : null,
      opening_leave_balance: form.employment_type==='Full Time' ? (parseFloat(form.opening_leave_balance) || 0) : null,
    };
    if(editId){
      const{error}=await supabase.from('team_members').update(payload).eq('id',editId);
      if(error){setErr('Save failed: '+error.message);setSaving(false);return;}
      setMembers(p=>p.map(m=>m.id===editId?{...m,...payload}:m));
    }else{
      const{data,error}=await supabase.from('team_members').insert(payload).select().single();
      if(error){setErr('Save failed: '+error.message);setSaving(false);return;}
      setMembers(p=>[...p,data].sort((a,b)=>a.name.localeCompare(b.name)));
      // Only now mark the source submission as converted — card is saved
      if(pendingSubmission?._submissionId){
        await supabase.from('new_hire_submissions').update({status:'converted'}).eq('id',pendingSubmission._submissionId);
        setSubmissions(p=>p.map(s=>s.id===pendingSubmission._submissionId?{...s,status:'converted'}:s));
      }
    }
    setShowForm(false);setEditId(null);setForm(emptyForm);setPendingSubmission(null);setSaving(false);
  }

  async function handleDelete(id){
    await supabase.from('team_members').delete().eq('id',id);
    setMembers(p=>p.filter(m=>m.id!==id));
    setDelConfirm(null);setModal(null);
  }

  async function handleDismissAlert(field, memberId) {
    const {error} = await supabase.from('team_members').update({[field]:true}).eq('id',memberId);
    if (!error) setMembers(p=>p.map(m=>m.id===memberId?{...m,[field]:true}:m));
  }

  const skillList=form.dept==='KIT'?KIT_SKILLS:BAR_SKILLS;

  const depositPaid   = members.filter(m => m.uniform_deposit_paid);
  const depositTotal  = depositPaid.reduce((s, m) => s + depositAmount(m.dept), 0);
  const depositPending = members.filter(m => !m.uniform_deposit_paid).length;

  return (
    <div style={{padding:'24px',maxWidth:'1100px',margin:'0 auto'}}>

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'20px',flexWrap:'wrap',gap:'10px'}}>
        <div>
          <h2 style={{margin:'0 0 4px',fontSize:'22px',fontWeight:700,color:C.charcoal}}>Team</h2>
          <p style={{margin:0,fontSize:'13px',color:C.mid}}>{members.length} team members across all venues</p>
        </div>
        {isMgr&&tab==='team'&&(
          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'6px'}}>
            <div style={{display:'flex',gap:'8px'}}>
              <button onClick={()=>setShowSend(true)} style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 14px',background:C.white,color:C.navy,border:`1px solid ${C.navy}`,borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'13px'}}>
                <Send size={14}/>Send New Hire Form
              </button>
              {isAdmin&&(
                <button onClick={()=>{setForm(emptyForm);setEditId(null);setShowForm(true);setErr(null);}}
                  style={{padding:'8px 16px',background:C.navy,color:C.white,border:`1px solid ${C.navy}`,borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'13px'}}>
                  + Add Member
                </button>
              )}
            </div>
            {isMgr&&!isMobile&&(
              <button onClick={()=>setShowDeposits(true)} style={{
                display:'flex',alignItems:'center',gap:'5px',
                padding:'4px 12px',borderRadius:'999px',
                border:`1px solid ${C.border}`,
                background:C.white,
                color:C.mid,
                fontSize:'11px',fontWeight:600,cursor:'pointer',
              }}>
                Uniform Deposits
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{display:'flex',gap:'4px',marginBottom:'24px',borderBottom:`2px solid ${C.border}`}}>
        {[
          {key:'team',  label:'Team Members', icon:<User size={14}/>, show:true},
          {key:'leave', label:'Leave',         icon:<Calendar size={14}/>, show:true},
          {key:'hours', label:'Hours',         icon:<Clock size={14}/>, show:isMgr},
          {key:'tips',  label:'Tips',          icon:<Award size={14}/>, show:isAdmin},
        ].filter(t=>t.show).map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)} style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 16px',border:'none',borderBottom:tab===t.key?`2px solid ${C.navy}`:'2px solid transparent',background:'none',cursor:'pointer',fontWeight:tab===t.key?700:400,color:tab===t.key?C.navy:C.mid,fontSize:'14px',marginBottom:'-2px'}}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab==='team'&&(
        <>
          {!showForm&&pendingSubmission&&isMgr&&(
  <div style={{background:C.amberLight,border:`1px solid ${C.amber}44`,borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px'}}>
    <span style={{fontSize:'13px',color:C.amber,fontWeight:600}}>
      ⚠️ Unsaved new hire: <strong>{pendingSubmission.name||'Unknown'}</strong>
    </span>
    <div style={{display:'flex',gap:'8px'}}>
      <button onClick={()=>{setForm(pendingSubmission);setShowForm(true);setErr(null);}}
        style={{padding:'6px 14px',background:C.amber,color:C.white,border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:600}}>
        Resume
      </button>
      <button onClick={()=>setPendingSubmission(null)}
        style={{padding:'6px 14px',background:'none',color:C.mid,border:`1px solid ${C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>
        Discard
      </button>
    </div>
  </div>
)}
{showForm&&isMgr&&(
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:600,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={cancelForm}>
    <div style={{background:C.white,borderRadius:'16px',width:'100%',maxWidth:'680px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 24px 64px rgba(0,0,0,0.22)'}} onClick={e=>e.stopPropagation()}>
      <div style={{padding:'18px 24px',background:C.navy,borderRadius:'16px 16px 0 0',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:1}}>
        <div>
          <div style={{fontWeight:700,fontSize:'15px',color:C.white}}>{editId?'Edit Team Member':'New Team Member'}</div>
          {editId&&<div style={{fontSize:'12px',color:C.skyBlue,marginTop:'2px'}}>{form.name}</div>}
        </div>
        <button onClick={cancelForm} style={{background:'rgba(255,255,255,0.12)',border:'none',borderRadius:'8px',cursor:'pointer',color:C.white,padding:'6px',display:'flex'}}><X size={18}/></button>
      </div>
      <div style={{padding:'24px'}}>
              {err&&<div style={{background:C.redLight,border:`1px solid ${C.red}44`,borderRadius:'6px',padding:'10px 14px',marginBottom:'16px',color:C.red,fontSize:'13px'}}>{err}</div>}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'12px',marginBottom:'16px'}}>
                <div style={{gridColumn:'span 2'}}><label style={lbl}>Full Name *</label><input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Jan de Vries" style={inp}/></div>
                <div><label style={lbl}>Venue</label><select value={form.venue} onChange={e=>setForm(p=>({...p,venue:e.target.value}))} style={inp}>{VENUES.map(v=><option key={v}>{v}</option>)}</select></div>
                <div><label style={lbl}>Dept</label><select value={form.dept} onChange={e=>setForm(p=>({...p,dept:e.target.value,skills:[]}))} style={inp}>{DEPTS.map(d=><option key={d}>{d}</option>)}</select></div>
                <div><label style={lbl}>Role / Job Title</label><input value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))} placeholder="e.g. Barista" style={inp}/></div>
                <div><label style={lbl}>Employment Type</label><select value={form.employment_type} onChange={e=>setForm(p=>({...p,employment_type:e.target.value}))} style={inp}>{EMP_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
                {form.employment_type==='Full Time'&&<div><label style={lbl}>Hours / Week</label><select value={form.contract_hours} onChange={e=>setForm(p=>({...p,contract_hours:parseInt(e.target.value)}))} style={inp}>{CONTRACT_HRS.map(h=><option key={h} value={h}>{h}hr</option>)}</select></div>}
                
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'12px',marginBottom:'16px'}}>
                <div><label style={lbl}>Contract Type</label><select value={form.contract_type} onChange={e=>setForm(p=>({...p,contract_type:e.target.value,contract_months:''}))} style={inp}>{CONTRACT_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
                <div><label style={lbl}>Contract #</label><input type="number" min="1" value={form.contract_number} onChange={e=>setForm(p=>({...p,contract_number:e.target.value}))} style={inp}/></div>
                <div><label style={lbl}>Join Date * <span style={{fontWeight:400,color:C.mid}}>(first day)</span></label><input type="date" value={form.join_date} onChange={e=>setForm(p=>({...p,join_date:e.target.value}))} style={inp}/></div>
                <div><label style={lbl}>Contract Start <span style={{fontWeight:400,color:C.mid}}>(if different)</span></label><input type="date" value={form.contract_start_date} onChange={e=>setForm(p=>({...p,contract_start_date:e.target.value}))} style={inp}/></div>
                {form.contract_type==='Fixed Term'&&<div><label style={lbl}>Contract Length (months)</label><input type="number" min="1" max="60" value={form.contract_months} onChange={e=>setForm(p=>({...p,contract_months:e.target.value}))} placeholder="e.g. 6" style={inp}/></div>}
                {form.contract_type==='Fixed Term'&&compEnd&&<div><label style={lbl}>Contract Ends</label><div style={{padding:'8px 10px',background:C.skyBlueLight,borderRadius:'8px',fontSize:'13px',color:C.skyText,fontWeight:600,border:`1px solid ${C.skyBlue}`}}>{fmt(compEnd)}</div></div>}
                <div><label style={lbl}>Birthday</label><input type="date" value={form.birthday||''} onChange={e=>setForm(p=>({...p,birthday:e.target.value}))} style={inp}/></div>
                <div style={{gridColumn:'span 2'}}><label style={lbl}>Email Address</label><input type="email" value={form.email||''} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="jan@email.com" style={inp}/></div>
              </div>



              {isAdmin && (
                <div style={{gridColumn:'span 2',paddingTop:'12px',borderTop:`1px solid ${C.border}`,marginTop:'4px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color:C.red,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Sensitive Data — Admin Only</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'12px'}}>
                    <div style={{gridColumn:'span 2'}}><label style={lbl}>Street Address</label><input value={form.street||''} onChange={e=>setForm(p=>({...p,street:e.target.value}))} placeholder="Bloemgracht 47" style={inp}/></div>
                    <div><label style={lbl}>Zip Code</label><input value={form.zip||''} onChange={e=>setForm(p=>({...p,zip:e.target.value}))} placeholder="1016 KD" style={inp}/></div>
                    <div><label style={lbl}>City</label><input value={form.city||''} onChange={e=>setForm(p=>({...p,city:e.target.value}))} placeholder="Amsterdam" style={inp}/></div>
                    <div><label style={lbl}>BSN</label><input value={form.bsn||''} onChange={e=>setForm(p=>({...p,bsn:e.target.value}))} placeholder="000000000" style={inp}/></div>
                    <div><label style={lbl}>IBAN</label><input value={form.iban||''} onChange={e=>setForm(p=>({...p,iban:e.target.value}))} placeholder="NL00 BANK 0000 0000 00" style={inp}/></div>
                    {form.employment_type==='Full Time'
                      ? <div><label style={lbl}>Gross Monthly Salary (€)</label><input type="number" min="0" step="0.01" value={form.gross_monthly_salary||''} onChange={e=>setForm(p=>({...p,gross_monthly_salary:e.target.value}))} placeholder="e.g. 2400.00" style={inp}/></div>
                      : <div><label style={lbl}>Hourly Rate (€)</label><input type="number" min="0" step="0.01" value={form.hourly_rate||''} onChange={e=>setForm(p=>({...p,hourly_rate:e.target.value}))} placeholder="e.g. 14.71" style={inp}/></div>
                    }
                    <div><label style={lbl}>NMBRS Employee # <span style={{fontWeight:400,color:C.mid}}>(payroll)</span></label><input type="number" min="1" value={form.nmbrs_employee_number||''} onChange={e=>setForm(p=>({...p,nmbrs_employee_number:e.target.value}))} placeholder="e.g. 42" style={inp}/></div>
                  </div>
                </div>
              )}
              <div style={{marginBottom:'16px'}}>
                <label style={lbl}>Skills</label>
                <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>{skillList.map(sk=><button key={sk} onClick={()=>toggleSkill(sk)} style={pill(form.skills.includes(sk))}>{sk}</button>)}</div>
              </div>
              <div style={{display:'flex',gap:'16px',flexWrap:'wrap',marginBottom:'16px',padding:'12px 14px',background:C.cream,borderRadius:'8px'}}>
                {[['probation_done','Onboarding complete'],['mid_checkin_done','Pulse check done'],['contract_renewal','Contract renewal done']].map(([f,l])=>(
                  <label key={f} style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'13px',cursor:'pointer',color:C.charcoal}}>
                    <input type="checkbox" checked={!!form[f]} onChange={e=>setForm(p=>({...p,[f]:e.target.checked}))}/>{l}
                  </label>
                ))}
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={handleSave} disabled={!form.name||!form.join_date||saving} style={{padding:'8px 20px',background:(!form.name||!form.join_date||saving)?C.border:C.navy,color:C.white,border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'13px'}}>
                  {saving?'Saving…':editId?'Update':'Save Member'}
                </button>
                <button onClick={cancelForm} style={{padding:'8px 16px',border:`1px solid ${C.border}`,borderRadius:'8px',background:C.white,cursor:'pointer',fontSize:'13px',color:C.mid}}>Cancel</button>
                {editId&&isAdmin&&<button onClick={()=>setDelConfirm(members.find(m=>m.id===editId))} style={{marginLeft:'auto',padding:'8px 16px',border:`1px solid ${C.red}44`,borderRadius:'8px',background:C.redLight,cursor:'pointer',fontSize:'13px',color:C.red}}>Remove Member</button>}
              </div>
      </div>
    </div>
  </div>
          )}

          {isMgr&&<NewHireBanner submissions={submissions} onOpen={()=>setShowSubmissions(true)}/>}
          {!loading&&<AlertSwimlanes members={members} onOpen={setModal} onDismiss={handleDismissAlert} isSupervisor={isSupervisor}/>}

          <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'20px'}}>
            {[...VENUES,'All'].map(v=>{
              const vm  = v==='All' ? members : members.filter(m=>m.venue===v);
              const ft  = vm.filter(m=>m.employment_type==='Full Time').length;
              const pt  = vm.filter(m=>m.employment_type==='Part Time').length;
              const foh = vm.filter(m=>m.dept==='FOH').length;
              const kit = vm.filter(m=>m.dept==='KIT').length;
              const mgt = vm.filter(m=>m.dept==='Management').length;
              const isActive = vf===v;
              const isTotal  = v==='All';
              return (
                <div key={v} onClick={()=>setVf(v)} style={{
                  flex:'1 1 100px', borderRadius:'12px', padding:'12px 14px',
                  border:`2px solid ${isActive?C.navy:C.border}`,
                  background: isActive?C.navy:C.white,
                  textAlign:'center', cursor:'pointer',
                  transition:'all 0.15s',
                  boxShadow: isActive?'0 2px 8px rgba(68,83,102,0.18)':'none',
                }}>
                  <div style={{fontSize:'22px',fontWeight:700,color:isActive?C.white:C.charcoal}}>{vm.length}</div>
                  <div style={{fontSize:'12px',color:isActive?C.skyBlue:C.mid,marginBottom:'4px'}}>
                    {isTotal?'Total':v.replace('Box ','')}
                  </div>
                  <div style={{fontSize:'10px',color:isActive?C.skyBlue+'99':C.border,marginBottom:'4px'}}>
                    {ft>0&&`${ft} FT`}{ft>0&&pt>0&&' · '}{pt>0&&`${pt} PT`}
                  </div>
                  <div style={{display:'flex',justifyContent:'center',gap:'3px',flexWrap:'wrap'}}>
                    {foh>0&&<span style={{fontSize:'10px',fontWeight:600,background:isActive?'rgba(255,255,255,0.15)':C.skyBlueLight,color:isActive?C.white:C.skyText,padding:'1px 5px',borderRadius:'20px'}}>{foh} FOH</span>}
                    {kit>0&&<span style={{fontSize:'10px',fontWeight:600,background:isActive?'rgba(255,255,255,0.15)':C.orangeLight,color:isActive?C.white:C.amber,padding:'1px 5px',borderRadius:'20px'}}>{kit} KIT</span>}
                    {mgt>0&&<span style={{fontSize:'10px',fontWeight:600,background:isActive?'rgba(255,255,255,0.15)':C.purpleLight,color:isActive?C.white:C.purple,padding:'1px 5px',borderRadius:'20px'}}>{mgt} MGT</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {isMobile ? (
            <div style={{marginBottom:'16px'}}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name…"
                style={{padding:'7px 14px',border:`1px solid ${C.border}`,borderRadius:'20px',fontSize:'13px',outline:'none',width:'100%',color:C.charcoal,boxSizing:'border-box',marginBottom:'10px'}}/>
              <div style={{display:'flex',overflowX:'auto',gap:'0',marginLeft:'-1rem',marginRight:'-1rem',paddingLeft:'1rem',paddingRight:'1rem',borderBottom:`1px solid ${C.border}`,scrollbarWidth:'none',WebkitOverflowScrolling:'touch'}}>
                {['All',...DEPTS].map(d=>{
                  const isActive=df===d;
                  return (
                    <button key={d} onClick={()=>setDf(d)} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'4px',padding:'0.5rem 1rem 0.55rem',background:'none',border:'none',cursor:'pointer',flexShrink:0,borderBottom:`2.5px solid ${isActive?C.navy:'transparent'}`,transition:'all 0.15s'}}>
                      <span style={{fontSize:'0.75rem',fontWeight:isActive?700:500,color:isActive?C.navy:C.mid,whiteSpace:'nowrap'}}>{d==='All'?'All Depts':d}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
          <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'16px',alignItems:'center'}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name…"
              style={{padding:'7px 14px',border:`1px solid ${C.border}`,borderRadius:'20px',fontSize:'13px',outline:'none',width:'180px',color:C.charcoal}}/>
            <div style={{display:'flex',gap:'4px',flexWrap:'wrap'}}>
              {['All',...DEPTS].map(d=>(
                <button key={d} onClick={()=>setDf(d)} style={pill(df===d,true)}>
                  {d==='All'?'All Depts':d}
                </button>
              ))}
            </div>
            {isAdmin&&(
              <div style={{display:'flex',gap:'6px',marginLeft:'auto',alignItems:'center'}}>
                <button onClick={()=>setNmbrsF(p=>!p)} style={{
                  padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:600,cursor:'pointer',
                  border:`1px solid ${nmbrsF?C.amber:C.border}`,
                  background:nmbrsF?C.amberLight:C.white,
                  color:nmbrsF?C.amber:C.mid,
                  transition:'all 0.15s',
                }}>NMBRS</button>
                <button onClick={()=>setContractF(p=>!p)} style={{
                  padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:600,cursor:'pointer',
                  border:`1px solid ${contractF?C.red:C.border}`,
                  background:contractF?C.redLight:C.white,
                  color:contractF?C.red:C.mid,
                  transition:'all 0.15s',
                }}>Contract</button>
                <button onClick={()=>setEitjeF(p=>!p)} style={{
                  padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:600,cursor:'pointer',
                  border:`1px solid ${eitjeF?C.skyText:C.border}`,
                  background:eitjeF?C.skyBlueLight:C.white,
                  color:eitjeF?C.skyText:C.mid,
                  transition:'all 0.15s',
                }}>Eitje</button>
              </div>
            )}
          </div>
          )}

          {loading?<div style={{padding:'40px',textAlign:'center',color:C.mid}}>Loading…</div>
          :filtered.length===0?<div style={{background:C.white,borderRadius:'10px',border:`1px solid ${C.border}`,padding:'40px',textAlign:'center',color:C.mid}}>No team members match this filter</div>
          :isMobile
            ? <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                {filtered.map(m=>(
                  <div key={m.id} onClick={()=>setModal(m)}
                    style={{display:'flex',alignItems:'center',gap:'10px',padding:'11px 14px',background:C.white,borderRadius:'10px',border:`1px solid ${C.border}`,cursor:'pointer'}}
                    onTouchStart={e=>e.currentTarget.style.background=C.cream}
                    onTouchEnd={e=>e.currentTarget.style.background=C.white}>
                    <RoleIcon dept={m.dept} size={28}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,fontSize:'14px',color:C.charcoal,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.name}</div>
                      <div style={{fontSize:'11px',color:C.mid}}>{m.venue.replace('Box ','')} · {m.role||m.dept}</div>
                    </div>
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M5 2.5L9.5 7L5 11.5" stroke={C.border} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                ))}
              </div>
            : <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>{filtered.map(m=><RosterRow key={m.id} member={m} onClick={setModal} isAdmin={isAdmin}/>)}</div>
          }
        </>
      )}

      {tab==='leave' && <LeaveCalendar members={members} currentUser={currentUser} />}
      {tab==='hours' && <LeaveOT members={members} currentUser={currentUser} onReloadMembers={loadMembers} />}
      {tab==='tips'  && isAdmin && <Tips currentUser={currentUser} />}

      {modal&&<MemberModal member={modal} onClose={()=>setModal(null)} onUpdate={onUpdate} currentUser={currentUser} trainings={trainings} completions={completions} onEdit={handleEdit}/>}
      {showSend&&<SendFormModal onClose={()=>setShowSend(false)} currentUser={currentUser} member={modal||undefined}/>}
      {showSubmissions&&<SubmissionsModal submissions={submissions} onClose={()=>setShowSubmissions(false)} isAdmin={isAdmin}
        onDismiss={id=>setSubmissions(p=>p.map(s=>s.id===id?{...s,status:'dismissed'}:s))}
        onConvert={sub=>{
  // Map submission into form — do NOT mark converted yet.
  // Conversion happens only after handleSave() succeeds.
  const mapped={...emptyForm,
    name:`${sub.first_name||''} ${sub.last_name||''}`.trim(),
    email:sub.email||'',
    phone:sub.phone||'',
    birthday:sub.dob ? (sub.dob.includes('/')?sub.dob.split('/').reverse().join('-'):sub.dob) : '',
    street:sub.street||'',
    zip:sub.zip||'',
    city:sub.city||'',
    bsn:sub.bsn||'',
    iban:sub.iban||'',
    nationality:sub.nationality||'',
    id_doc_path:sub.id_doc_path||'',
    bsn_doc_path:sub.bsn_doc_path||'',
    // Pre-fill from manager-entered fields
    venue:sub.venue||'Box Jordaan',
    dept:sub.dept||'FOH',
    role:sub.role||'',
    employment_type:sub.employment_type||'Full Time',
    contract_hours:sub.contract_hours||38,
    contract_start_date:sub.contract_start_date||'',
    join_date:sub.contract_start_date||'',
    gross_monthly_salary:sub.gross_monthly_salary||'',
    hourly_rate:sub.hourly_rate||'',
  };
  setPendingSubmission({...mapped, _submissionId: sub.id});
  setForm(mapped);
  setEditId(null);setShowForm(true);setErr(null);
  setShowSubmissions(false);
}}
      />}

      {showDeposits&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:600,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={()=>setShowDeposits(false)}>
          <div style={{background:C.white,borderRadius:'16px',width:'100%',maxWidth:'520px',maxHeight:'85vh',overflowY:'auto',boxShadow:'0 24px 64px rgba(0,0,0,0.22)'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'18px 24px',background:C.navy,borderRadius:'16px 16px 0 0',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:1}}>
              <div>
                <div style={{fontWeight:700,fontSize:'15px',color:C.white}}>Uniform Deposits</div>
                <div style={{fontSize:'12px',color:C.skyBlue,marginTop:'2px'}}>FOH €15 · KIT €200 · Total collected: <strong>€{depositTotal}</strong></div>
              </div>
              <button onClick={()=>setShowDeposits(false)} style={{background:'rgba(255,255,255,0.12)',border:'none',borderRadius:'8px',cursor:'pointer',color:C.white,padding:'6px',display:'flex'}}><X size={18}/></button>
            </div>
            <div style={{padding:'20px 24px'}}>
              {depositPending>0&&(
                <div style={{marginBottom:'20px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color:C.amber,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'8px',display:'flex',alignItems:'center',gap:'6px'}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:C.amber,display:'inline-block'}}/>
                    Pending ({depositPending})
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                    {members.filter(m=>!m.uniform_deposit_paid).map(m=>(
                      <div key={m.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',background:C.cream,borderRadius:'8px',border:`1px solid ${C.border}`}}>
                        <RoleIcon dept={m.dept} size={28}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:'13px',fontWeight:600,color:C.charcoal}}>{m.name}</div>
                          <div style={{fontSize:'11px',color:C.mid}}>{m.venue.replace('Box ','')} · {m.dept}</div>
                        </div>
                        <span style={{fontSize:'12px',fontWeight:700,color:C.mid}}>€{depositAmount(m.dept)}</span>
                        {isAdmin&&(
                          <button onClick={async()=>{
                            const{error}=await supabase.from('team_members').update({uniform_deposit_paid:true}).eq('id',m.id);
                            if(!error)setMembers(p=>p.map(x=>x.id===m.id?{...x,uniform_deposit_paid:true}:x));
                          }} style={{padding:'4px 10px',background:C.navy,color:C.white,border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:600,whiteSpace:'nowrap'}}>
                            Mark Paid
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {depositPaid.length>0&&(
                <div>
                  <div style={{fontSize:'11px',fontWeight:700,color:C.green,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'8px',display:'flex',alignItems:'center',gap:'6px'}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:C.green,display:'inline-block'}}/>
                    Collected ({depositPaid.length}) · €{depositTotal}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                    {depositPaid.map(m=>(
                      <div key={m.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',background:C.greenLight,borderRadius:'8px',border:`1px solid ${C.green}33`}}>
                        <RoleIcon dept={m.dept} size={28}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:'13px',fontWeight:600,color:C.charcoal}}>{m.name}</div>
                          <div style={{fontSize:'11px',color:C.mid}}>{m.venue.replace('Box ','')} · {m.dept}</div>
                        </div>
                        <span style={{fontSize:'12px',fontWeight:700,color:C.green}}>€{depositAmount(m.dept)} ✓</span>
                        {isAdmin&&(
                          <button onClick={async()=>{
                            const{error}=await supabase.from('team_members').update({uniform_deposit_paid:false}).eq('id',m.id);
                            if(!error)setMembers(p=>p.map(x=>x.id===m.id?{...x,uniform_deposit_paid:false}:x));
                          }} style={{padding:'4px 10px',background:'none',color:C.mid,border:`1px solid ${C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'11px',whiteSpace:'nowrap'}}>
                            Undo
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {delConfirm&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:600,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={()=>setDelConfirm(null)}>
          <div style={{background:C.white,borderRadius:'14px',padding:'24px',width:'360px',maxWidth:'100%',boxShadow:'0 8px 32px rgba(0,0,0,0.18)'}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:'0 0 10px',fontSize:'15px',fontWeight:700,color:C.charcoal}}>Remove Team Member</h3>
            <p style={{fontSize:'13px',color:C.mid,marginBottom:'20px'}}>Remove <strong>{delConfirm?.name}</strong>? This cannot be undone.</p>
            <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
              <button onClick={()=>setDelConfirm(null)} style={pill(false)}>Cancel</button>
              <button onClick={()=>handleDelete(delConfirm.id)} style={{padding:'6px 16px',background:C.red,color:C.white,border:'none',borderRadius:'20px',cursor:'pointer',fontSize:'13px',fontWeight:600}}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}